import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Link2, HeartHandshake, Clock, ExternalLink, Share2, MapPin, Calendar, Wifi, CalendarPlus, Inbox, Trash2, Settings } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { OnboardingWizard } from "../components/OnboardingWizard";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { InquiryInbox } from "../components/InquiryInbox";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { connectionMeta, formatDateRange, toExternalUrl } from "../lib/meta";
import { downloadInitiativeIcs } from "../lib/calendar";
import { BookingConfig } from "../components/BookingConfig";
import { PsychologistAgenda } from "../components/PsychologistAgenda";
import { CalendarClock } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

export default function PsychologistWorkspace() {
  const { t, lang } = useLang();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(undefined);
  const [myInit, setMyInit] = useState([]);
  const [myFilter, setMyFilter] = useState("all");
  const [myType, setMyType] = useState("all");

  const loadProfile = () => api.get("/psychologists/me").then((r) => setProfile(r.data)).catch(() => setProfile(null));
  useEffect(() => {
    loadProfile();
    api.get("/psychologists/me/initiatives").then((r) => setMyInit(r.data)).catch(() => {});
  }, []);

  const onSaved = async (data) => {
    setProfile(data);
    await refresh();
    toast.success(t("workspace.profile_saved"));
  };

  const [deleting, setDeleting] = useState(false);
  const handleDeleteProfile = async () => {
    setDeleting(true);
    try {
      await api.delete("/psychologists/me");
      toast.success(t("del.success"));
      setProfile(null);
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.error === "has_linked_activity" ? t("del.blocked_body") : t("del.error"));
    } finally {
      setDeleting(false);
    }
  };

  if (profile === undefined) return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">{t("common.loading")}</div>;

  if (profile === null) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <EmptyState icon={User} title={t("workspace.no_profile_title")} description={t("workspace.no_profile_desc")}
          action={<Button onClick={() => navigate("/unirme")} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="create-profile-btn">{t("workspace.create_profile")}</Button>} />
      </div>
    );
  }

  const statusMsg = {
    draft: t("workspace.status_draft"),
    pending: t("workspace.status_pending"), verified: t("workspace.status_verified"),
    rejected: t("workspace.status_rejected"), suspended: t("workspace.status_suspended"),
    changes: t("workspace.status_changes_short"),
  }[profile.status];

  const visibleParts = myInit.filter((p) => {
    if (myFilter !== "all" && p.status !== myFilter) return false;
    const isOpp = !!p.initiative?.is_opportunity;
    if (myType === "opportunities") return isOpp;
    if (myType === "initiatives") return !isOpp;
    return true;
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("workspace.title")}</h1>
        <div className="flex items-center gap-2">
          {profile.status === "verified" && (
            <Button variant="outline" size="sm" className="rounded-full" data-testid="workspace-share-btn"
              onClick={() => {
                const url = `${window.location.origin}/profesionales/${profile.id}`;
                navigator.clipboard?.writeText(url).then(() => toast.success(t("common.link_copied"))).catch(() => {});
              }}>
              <Share2 className="mr-1.5 h-3.5 w-3.5" /> {t("common.share")}
            </Button>
          )}
          <StatusBadge status={profile.status} label={t(`status.${profile.status}`)} />
        </div>
      </div>
      <p className="mt-3 rounded-xl border border-border bg-white px-4 py-3 text-sm text-slateink/80" data-testid="workspace-status-msg">{statusMsg}</p>
      {profile.status === "draft" && (
        <div className="mt-3 rounded-xl border border-slateink/20 bg-cream px-4 py-3" data-testid="workspace-draft-banner">
          <p className="text-sm text-slateink/80">{t("workspace.draft_banner")}</p>
          <Button size="sm" className="mt-3 rounded-full bg-terracotta hover:bg-terracotta-hover" onClick={() => navigate("/unirme")} data-testid="continue-onboarding-btn">{t("workspace.continue_onboarding")}</Button>
        </div>
      )}
      {profile.status === "changes" && (
        <div className="mt-3 rounded-xl border border-terracotta bg-terracotta-light px-4 py-3" data-testid="workspace-changes-banner">
          <p className="text-sm font-semibold text-terracotta">{t("workspace.changes_banner_title")}</p>
          <p className="mt-1 text-sm text-slateink/70">{t("workspace.status_changes")}</p>
          {profile.changes_requested_note && (
            <p className="mt-2 rounded-lg bg-white/70 px-3 py-2 text-sm text-slateink" data-testid="workspace-changes-note"><b>{t("workspace.requested_changes_label")}</b> {profile.changes_requested_note}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-hover" onClick={() => navigate("/unirme")} data-testid="update-profile-btn">{t("workspace.update_profile")}</Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="profile" className="mt-8">
        <TabsList className="h-auto flex-wrap gap-1">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="mr-1.5 h-4 w-4" />{t("workspace.profile")}</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links"><Link2 className="mr-1.5 h-4 w-4" />{t("workspace.links")}</TabsTrigger>
          <TabsTrigger value="initiatives" data-testid="tab-initiatives"><HeartHandshake className="mr-1.5 h-4 w-4" />{t("workspace.initiatives")}</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox"><Inbox className="mr-1.5 h-4 w-4" />{t("inbox.nav")}</TabsTrigger>
          {profile.status === "verified" && <TabsTrigger value="agenda" data-testid="tab-agenda"><CalendarClock className="mr-1.5 h-4 w-4" />{t("booking.agenda")}</TabsTrigger>}
          <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="mr-1.5 h-4 w-4" />{t("workspace.settings")}</TabsTrigger>
        </TabsList>

        {profile.status === "verified" && (
          <TabsContent value="agenda" className="mt-6">
            <Tabs defaultValue="appointments">
              <TabsList>
                <TabsTrigger value="appointments" data-testid="agenda-sub-appts">{t("booking.appointments")}</TabsTrigger>
                <TabsTrigger value="availability" data-testid="agenda-sub-avail">{t("booking.availability")}</TabsTrigger>
              </TabsList>
              <TabsContent value="appointments" className="mt-6"><PsychologistAgenda /></TabsContent>
              <TabsContent value="availability" className="mt-6"><BookingConfig /></TabsContent>
            </Tabs>
          </TabsContent>
        )}

        <TabsContent value="profile" className="mt-6">
          <OnboardingWizard initial={profile} startStage={0} />
        </TabsContent>

        <TabsContent value="links" className="mt-6">
          <div className="rounded-2xl border border-border bg-white p-6 sm:p-8">
            <p className="text-sm text-muted-foreground">{t("join.section_connection")}</p>
            <div className="mt-4 space-y-2">
              {(profile.connection_methods || []).length === 0 && <p className="text-sm text-muted-foreground">{t("common.none")}</p>}
              {(profile.connection_methods || []).map((c, i) => {
                const M = connectionMeta(c.type);
                const isPrimary = c.url === profile.primary_connection;
                return (
                  <a key={i} href={toExternalUrl(c.url)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-xl border border-border px-4 py-3 text-sm hover:border-terracotta/40">
                    <span className="inline-flex items-center gap-2 text-slateink"><M.icon className="h-4 w-4" />{M.label}{isPrimary && <span className="rounded-full bg-terracotta px-2 py-0.5 text-xs text-white">{t("join.primary_method")}</span>}</span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </a>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{t("workspace.profile")} → {t("join.section_connection")}</p>
          </div>
        </TabsContent>

        <TabsContent value="initiatives" className="mt-6">
          {myInit.length === 0 ? (
            <EmptyState icon={HeartHandshake} title={t("workspace.no_initiatives")} testId="workspace-no-initiatives" />
          ) : (
            <>
              <div className="mb-4 flex w-max max-w-full flex-wrap gap-1 rounded-full border border-border bg-white p-1" data-testid="my-part-type-tabs">
                {[["all", t("workspace.participations_all")], ["initiatives", t("workspace.participations_initiatives")], ["opportunities", t("workspace.participations_opportunities")]].map(([v, l]) => (
                  <button key={v} onClick={() => setMyType(v)} data-testid={`my-part-type-${v}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${myType === v ? "bg-terracotta text-white" : "text-slateink/70"}`}>{l}</button>
                ))}
              </div>
              <div className="mb-4 flex w-max max-w-full flex-wrap gap-1 rounded-full border border-border bg-white p-1">
                {[["all", t("workspace.participations_all")], ["pending", t("status.pending")], ["confirmed", t("status.confirmed")], ["declined", t("status.declined")]].map(([v, l]) => (
                  <button key={v} onClick={() => setMyFilter(v)} data-testid={`my-part-filter-${v}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${myFilter === v ? "bg-slateink text-white" : "text-slateink/70"}`}>{l}</button>
                ))}
              </div>
              {visibleParts.length === 0 ? (
                <EmptyState icon={HeartHandshake} title={t("workspace.no_initiatives")} testId="workspace-no-initiatives-filtered" />
              ) : (
                <div className="space-y-3">
                  {visibleParts.map((p) => {
                    const init = p.initiative || {};
                    const dates = formatDateRange(init.start_date, init.end_date, lang) || init.dates;
                    const modality = (init.online_required && init.in_person_required) ? t("initiatives.online_and_in_person")
                      : init.online_required ? t("common.online") : init.in_person_required ? t("common.in_person") : null;
                    return (
                      <div key={p.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`my-initiative-${p.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-display font-semibold text-slateink">{init.name || "—"}</p>
                          {init.is_opportunity && <span className="rounded-full bg-terracotta-light px-2 py-0.5 text-[11px] font-semibold text-terracotta" data-testid={`opp-badge-${p.id}`}>{t("opportunities.badge")}</span>}
                        </div>
                        <StatusBadge status={p.status} label={t(`status.${p.status}`)} />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {init.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{init.location}</span>}
                          {dates && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{dates}</span>}
                          {modality && <span className="inline-flex items-center gap-1"><Wifi className="h-3.5 w-3.5" />{modality}</span>}
                          {init.status === "closed" && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t("status.closed")}</span>}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {init.id && (
                            <Button asChild size="sm" variant="outline" data-testid={`view-init-${p.id}`}>
                              <a href={`/iniciativas/${init.id}`} target="_blank" rel="noopener noreferrer">{t("workspace.view_initiative")} <ExternalLink className="ml-1 h-3.5 w-3.5" /></a>
                            </Button>
                          )}
                          {p.status === "confirmed" && init.start_date && (
                            <Button size="sm" variant="outline" onClick={() => downloadInitiativeIcs(init, { t, lang })} data-testid={`my-cal-${p.id}`}>
                              <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />{t("initiatives.add_to_calendar")}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="inbox" className="mt-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-slateink">{t("inbox.title_prof")}</h2>
          <InquiryInbox mode="received" />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6" data-testid="workspace-danger-zone">
            <p className="font-display text-sm font-semibold text-destructive">{t("del.section_title")}</p>
            <p className="mt-1 text-sm text-slateink/70">{t("del.pro_desc")}</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="mt-4 rounded-full border-destructive text-destructive hover:bg-destructive hover:text-white" data-testid="delete-profile-btn">
                  <Trash2 className="mr-1.5 h-4 w-4" />{t("del.pro_button")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-profile-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("del.confirm_title")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("del.confirm_body")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-profile-cancel">{t("del.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteProfile} disabled={deleting}
                    className="bg-destructive text-white hover:bg-destructive/90" data-testid="delete-profile-confirm">
                    {t("del.confirm_action")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
